import { Alert, Button, Cell, FormControl, Grid, Heading, Tag, Text, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import useSession from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { PasswordField, TextField } from 'components/form'
import { Form, FormRenderProps } from 'components/form/final-form/Form'
import { Cpf } from 'components/label'
import { FormApi } from 'final-form'
import {
  useCadastrarEnderecoEnvioMutation,
  useCancelarSolicitacaoEnvioMutation,
  useDadosServidoresQuery,
  useInicarUnificacaoBaseMutation,
  usePedidoEnvioBaseAtivoQuery,
  usePedidosEnvioBaseQuery,
  useUnificacoesBaseQuery,
} from 'graphql/hooks.generated'
import { useCapslockState } from 'hooks/useCapslockState'
import React, { useState } from 'react'
import { metaPath } from 'util/metaPath'
import { createValidator, maxLength, required } from 'util/validation'

import { unificacaoAtivaList } from './model'
import { SolicitacaoEnvioDadosTable } from './SolicitacaoEnvioDadosTable'

interface EnvioDadosViewProps {
  conexaoInternet: boolean
}

export interface SolicitacaoEnvioModel {
  nomeInstalacao: string
  linkInstalacao?: string
  senhaInstalacao?: string
}

const validator = createValidator<SolicitacaoEnvioModel>({
  nomeInstalacao: [required, maxLength(255)],
  linkInstalacao: [required, maxLength(255)],
  senhaInstalacao: [required, maxLength(255)],
})

export const EnvioDadosView = (props: EnvioDadosViewProps) => {
  const { conexaoInternet } = props

  const path = metaPath<SolicitacaoEnvioModel>()
  const { data: dataUser } = useSession()
  const isCapsOn = useCapslockState()
  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const [isCamposAtivos, setIsCamposAtivos] = useState<boolean>(true)
  const [hasUnificacaoAtiva, setHasUnificacaoAtiva] = useState<boolean>(false)
  const [errorResponseMessage, setErrorResponseMessage] = useState<JSX.Element>()
  const [errorLoginMessage, setErrorLoginMessage] = useState<JSX.Element>()
  const disabled = !isCamposAtivos || hasUnificacaoAtiva
  const [cadastrarPedidoEnvio] = useCadastrarEnderecoEnvioMutation()
  const [initialValues, setInitialValues] = useState<SolicitacaoEnvioModel>()
  const [cancelarSolicitacaoEnvio] = useCancelarSolicitacaoEnvioMutation()
  const [iniciarUnificacaoBase] = useInicarUnificacaoBaseMutation()
  const { data: dataPedidosEnvio, refetch } = usePedidosEnvioBaseQuery()
  const { data: dataServidor } = useDadosServidoresQuery({
    onCompleted: (data) => {
      setInitialValues({ nomeInstalacao: data.servidores?.dadosServidor.nomeInstalacao })
    },
  })

  usePedidoEnvioBaseAtivoQuery({
    onCompleted: (data) => {
      if (data.pedidoEnvioBaseAtivo) {
        setIsCamposAtivos(false)
        setInitialValues({
          nomeInstalacao: data.pedidoEnvioBaseAtivo.nomeInstalacao,
          linkInstalacao: data.pedidoEnvioBaseAtivo.linkInstalacao,
        })
      }
    },
  })

  useUnificacoesBaseQuery({
    onCompleted: (data) => {
      const unificacoesAtivas = data.unificacoesBase?.filter((item) => {
        return unificacaoAtivaList.includes(item.status)
      })
      setHasUnificacaoAtiva(unificacoesAtivas.length > 0)
    },
  })

  const onSubmit = (values: SolicitacaoEnvioModel, formApi: FormApi<SolicitacaoEnvioModel>) => {
    if (!conexaoInternet) {
      alert(
        'warning',
        'Não é possível realizar a solicitação de envio de dados sem conexão com a internet. Habilite a conexão ou tente novamente mais tarde.'
      )
    } else {
      cadastrarPedidoEnvio({
        variables: {
          input: { ...values },
        },
      })
        .then((data) => {
          if (data.data.cadastrarEnderecoEnvio.errorCnesUnidadesSaude) {
            setErrorResponseMessage(errorUnidadeSaudeAlert(data.data.cadastrarEnderecoEnvio.errorCnesUnidadesSaude))
            setErrorLoginMessage(null)
          } else if (data.data.cadastrarEnderecoEnvio.errorEquipes) {
            setErrorResponseMessage(errorEquipeAlert(data.data.cadastrarEnderecoEnvio.errorEquipes))
            setErrorLoginMessage(null)
          } else if (data.data.cadastrarEnderecoEnvio.errorLogin) {
            setErrorResponseMessage(null)
            setErrorLoginMessage(errorLoginAlert(data.data.cadastrarEnderecoEnvio.errorLogin))
          } else {
            alert('success', 'Solicitação enviada com sucesso.')
            setErrorResponseMessage(null)
            setErrorLoginMessage(null)
            setIsCamposAtivos(false)
            formApi.change(path.senhaInstalacao.alias, null)
            formApi.resetFieldState(path.senhaInstalacao.alias)
            refetch()
          }
        })
        .catch(handleRejection)
    }
  }

  const onUnificarBase = () => {
    iniciarUnificacaoBase().then(refetch).catch(handleRejection)
  }

  const errorUnidadeSaudeAlert = (error: String[]) => {
    return (
      <Cell size={12}>
        <Alert type='danger' inline>
          <p>
            A instalação central do município não possui o(s) seguinte(s) CNES: {error}
            .<br />
            Entre em contato com o suporte para dar continuidade ao processo de unificação de base.
          </p>
        </Alert>
      </Cell>
    )
  }

  const errorEquipeAlert = (error: String[]) => {
    return (
      <Cell size={12}>
        <Alert type='danger' inline>
          <p>
            Entre em contato com o suporte para dar continuidade ao processo de unificação de base. A relação de
            estabelecimentos e equipes não encontradas na instalação central do município pode ser visualizada abaixo:
            <br />
            {error.map((item) => {
              return (
                <span>
                  {item} <br />
                </span>
              )
            })}
          </p>
        </Alert>
      </Cell>
    )
  }

  const errorLoginAlert = (error: String[]) => {
    return (
      <Cell size={12}>
        <Alert type='danger' inline>
          {error}
        </Alert>
      </Cell>
    )
  }

  const renderForm = (formProps: FormRenderProps<SolicitacaoEnvioModel>) => {
    const onCancelarSolicitacao = (solicitacaoId) => {
      cancelarSolicitacaoEnvio({ variables: { input: { solicitacaoId: solicitacaoId } } })
        .then(() => {
          setIsCamposAtivos(true)
          refetch()
          setInitialValues({ nomeInstalacao: dataServidor.servidores?.dadosServidor.nomeInstalacao })
          formProps.form.reset()
          formProps.form.getRegisteredFields().forEach((field) => formProps.form.resetFieldState(field))
        })
        .catch(handleRejection)
    }

    return (
      <VFlow vSpacing={1}>
        <Grid gap={2}>
          <Cell size={12}>
            <Heading level={2}>Solicitação de envio para a instalação central do município</Heading>
          </Cell>
          {hasUnificacaoAtiva && (
            <Cell size={12}>
              <Alert type='warning' inline>
                Não é possível solicitar uma unificação de base enquanto possui unificações em andamento. É necessário
                finalizar todos os recebimentos para poder realizar uma solicitação.
              </Alert>
            </Cell>
          )}
          {errorResponseMessage}
          <Cell size={12}>
            <Heading level={3}>Dados de envio</Heading>
          </Cell>
          <Cell size={6}>
            <TextField
              label='Nome da minha instalação'
              name={path.nomeInstalacao}
              required
              clearable
              disabled={disabled}
              maxLength={255}
            />
          </Cell>
          <Cell size={6}>
            <TextField
              label='Link da instalação central'
              name={path.linkInstalacao}
              required
              clearable
              disabled={disabled}
              maxLength={255}
            />
          </Cell>
          <Cell size={12} style={{ marginTop: '0.5rem' }}>
            <Heading level={3}>Dados de segurança</Heading>
          </Cell>
          <Cell size={12}>
            <Text>
              Para fazer a solicitação de unificação é necessário a validação com a senha do login da <br />
              instalação central do município. Você já está conectado com o{' '}
              <b>
                CPF <Cpf value={dataUser.profissional.cpf} />.
              </b>
            </Text>
          </Cell>
          <Cell size={4}>
            <VFlow vSpacing={0.5}>
              <PasswordField
                label='Senha na instalação central'
                name={path.senhaInstalacao}
                required
                clearable
                disabled={disabled}
                maxLength={255}
              />
              {isCapsOn && <Tag style={{ display: 'inline-block' }}>Caps lock ativado</Tag>}
            </VFlow>
          </Cell>
          <Cell size={2}>
            <FormControl label={<span>&nbsp;</span>}>
              <Button kind='primary' size='small' disabled={disabled} onClick={formProps.handleSubmit}>
                Solicitar unificação
              </Button>
            </FormControl>
          </Cell>
          {errorLoginMessage}
          <Cell size={12} style={{ marginTop: '1rem' }}>
            <Heading level={3}>Processo de envio para a instalação central do município</Heading>
          </Cell>
          <Cell size={12}>
            <Alert type='warning' inline>
              Ao unificar a sua base com a instalação central do município, seu sistema será desativado e o novo link de
              acesso esus APS será repassado pelo administrador da instalação central.
            </Alert>
          </Cell>
          {dataPedidosEnvio.pedidosEnvioBase?.length === 0 ? (
            <Cell size={12}>
              <Text>Nenhuma solicitação de unificação realizada.</Text>
            </Cell>
          ) : (
            <Cell size={12}>
              <SolicitacaoEnvioDadosTable
                data={dataPedidosEnvio}
                hasUnificacaoAtiva={hasUnificacaoAtiva}
                onCancelarSolicitacao={onCancelarSolicitacao}
                onUnificarBase={onUnificarBase}
              />
            </Cell>
          )}
        </Grid>
      </VFlow>
    )
  }
  return (
    <Form<SolicitacaoEnvioModel>
      onSubmit={onSubmit}
      render={renderForm}
      validate={validator}
      initialValues={initialValues}
      {...props}
    />
  )
}
